import { useOasisBackend } from "../hooks/useOasisBackend"
import { useState, useEffect } from "react"
import { IconCopy, IconTrash } from "@tabler/icons-react"
import { toast } from "react-hot-toast"

const APIKeyManager = () => {
    const db = useOasisBackend()
    const [tokens, setTokens] = useState<{id: number, public_key: string, name: string, created_at: string}[]>([])
    const [newPublicKey, setNewPublicKey] = useState("")
    const [newPrivateKey, setNewPrivateKey] = useState("")

    const getAPIKeys = async () => {
        const response = await db.endpoints.api.apiPublicPrivateKeyRetrieve()
        setTokens(response.data)
    }

    useEffect(() => {
        getAPIKeys()
    }, [])

    const createAPIKeys = async () => {
        const response = await db.endpoints.api.apiPublicPrivateKeyCreate()
        if (response.status === 200) {
            setNewPublicKey(response.data.public_token)
            setNewPrivateKey(response.data.private_token)
            await getAPIKeys() // Refresh the list of tokens
        }
    }

    const deleteToken = async (id: number) => {
        try {
            await db.endpoints.api.apiPublicPrivateKeyDeleteDestroy(id)
            await getAPIKeys() // Refresh the list
            toast.success("Token deleted successfully")
        } catch (error) {
            toast.error("Failed to delete token")
        }
    }
    
    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text)
        toast.success("Copied to clipboard")
    }

    return (
        <div className="max-w-3xl mx-auto p-6">
            <h1 className="text-2xl font-bold mb-6">API Key Manager</h1>
            
            {/* New Keys Section */}
            <div className="mb-8">
                <h2 className="text-lg font-semibold mb-4">Generate New API Keys</h2>
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gray-50">
                                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Key Type</th>
                                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Value</th>
                                <th className="px-6 py-3 w-16"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            <tr>
                                <td className="px-6 py-4 text-sm font-medium text-gray-700">Public Key</td>
                                <td className="px-6 py-4 text-sm text-gray-500 font-mono flex flex-row items-center gap-2">
                                    <div>{newPublicKey}</div>
                                    {newPublicKey && (
                                        <button 
                                            onClick={() => copyToClipboard(newPublicKey)}
                                            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                                            title="Copy to clipboard"
                                        >
                                            <IconCopy className="text-gray-700" size={18} />
                                        </button>
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <td className="px-6 py-4 text-sm font-medium text-gray-700">Private Key</td>
                                <td className="px-6 py-4 text-sm text-gray-500 font-mono">
                                    <div className="flex flex-row items-center gap-2">
                                        <div>{newPrivateKey || "XXXX"}</div>
                                        {newPrivateKey && (
                                            <button 
                                                onClick={() => copyToClipboard(newPrivateKey)}
                                                className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                                                title="Copy to clipboard"
                                            >
                                                <IconCopy className="text-gray-700" size={18} />
                                            </button>
                                        )}
                                    </div>
                                    {newPrivateKey && (
                                        <p className="text-amber-600 mt-1 text-xs">
                                            We will never show your private key again. Please save it now.
                                        </p>
                                    )}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="mt-6">
                    {newPublicKey && (
                        <p className="text-sm text-gray-500 mb-3">
                            Generating new keys will invalidate the current keys.
                        </p>
                    )}
                    <button 
                        onClick={createAPIKeys}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                    >
                        Generate New API Keys
                    </button>
                </div>
            </div>

            {/* Existing Keys Section */}
            <div>
                <h2 className="text-lg font-semibold mb-4">Existing API Keys</h2>
                <div className="bg-white rounded-lg shadow overflow-x-auto">
                    <table className="w-full min-w-[600px]">
                        <thead>
                            <tr className="bg-gray-50">
                                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Created At</th>
                                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Public Key</th>
                                <th className="px-6 py-3 w-16"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {tokens.map((token) => (
                                <tr key={token.id}>
                                    <td className="px-6 py-4 text-sm text-gray-500">
                                        {new Date(token.created_at).toLocaleString()}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500 font-mono flex flex-row items-center gap-2 break-all max-w-xs">
                                        <div className="truncate max-w-[250px]">{token.public_key}</div>
                                        <button 
                                            onClick={() => copyToClipboard(token.public_key)}
                                            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                                            title="Copy to clipboard"
                                        >
                                            <IconCopy className="text-gray-700" size={18} />
                                        </button>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500">
                                        <button
                                            onClick={() => deleteToken(token.id)}
                                            className="p-1.5 hover:bg-red-50 rounded transition-colors text-red-600"
                                            title="Delete token"
                                        >
                                            <IconTrash size={18} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {tokens.length === 0 && (
                                <tr>
                                    <td colSpan={3} className="px-6 py-4 text-sm text-gray-500 text-center">
                                        No existing API keys found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}

export default APIKeyManager